import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import { useCoinPriceAPIStore, useInfoAPIStore } from '@/stores'
import type { Maybe } from '@/types'
import { AccountToken } from '@/types/account/AccountToken'
import type {
  IAccountToken,
  IAddTokenRequest,
  IGetTokenListRequest,
  IUpdateTokenRequest,
} from '@/types/api'
import type { IAccount } from '@/types/api/wallet'
import { getExecuteOnce } from '@/utils'

const useAccountTokenAPIStore = defineStore('accountTokenAPI', () => {
  const coinPriceAPIStore = useCoinPriceAPIStore()
  const infoAPIStore = useInfoAPIStore()
  const accountTokenList = computed<Maybe<IAccountToken[]>>(() => {
    const allTokens = Object.values(accountTokenListByAccountId.value)
      .flat()
      .filter((val) => !!val) as IAccountToken[]

    // 마이그레이션 데이터 중복 대응: 같은 주소 + 같은 label을 가진 토큰은 첫 번째만 사용
    const seenIds = new Set<string>()
    const seenAddressLabel = new Set<string>()
    const uniqueTokens = allTokens.filter((token) => {
      if (seenIds.has(token.id)) {
        return false
      }

      const address = token.addresses?.[0] || (token as any).address || ''
      const addressLabelKey = `${token.parentAccountId || token.accountId}-${address}-${token.label}`
      if (seenAddressLabel.has(addressLabelKey)) {
        return false
      }

      seenIds.add(token.id)
      seenAddressLabel.add(addressLabelKey)
      return true
    })

    return uniqueTokens
  })
  const accountTokenListByAccountId = ref<
    Record<string, Maybe<IAccountToken[]>>
  >({})

  const addToken = async (params: IAddTokenRequest) => {
    if (!params) return
    const res =
      await WepinBackendAPI.getInstance().accountToken.addToken(params)
    if (!res) return

    let accountToken: IAccountToken
    if (Array.isArray(res)) {
      accountToken = res[0] as IAccountToken
    } else if (res.accountToken) {
      accountToken = res.accountToken as IAccountToken
    } else {
      return
    }

    const accountInstance = new AccountToken(accountToken)
    accountInstance.fetchBalance()

    coinPriceAPIStore.fetchCoinPrice({
      coinGeckoIds: [accountInstance.coin?.coinGeckoId as string],
      currency: infoAPIStore.currentCurrency,
    })

    const accountId = (params as any).parentAccountId || params.accountId
    if (!accountTokenListByAccountId.value[accountId]) {
      accountTokenListByAccountId.value[accountId] = []
    }
    accountTokenListByAccountId.value[accountId]?.push(accountToken)
  }

  const deleteToken = async (
    id: IAccountToken['id'],
    walletId?: string,
    skipRefresh = false,
  ) => {
    if (!id) return
    const res = await WepinBackendAPI.getInstance().accountToken.deleteToken({
      id,
      walletId,
    })

    if (res.result && !skipRefresh) {
      const { useWalletAPIStore } = await import('@/stores')
      const walletAPIStore = useWalletAPIStore()
      await walletAPIStore.fetchWalletList({}, true)
    }
    return res
  }

  const updateToken = async (
    id: IAccountToken['id'],
    body: IUpdateTokenRequest,
  ) => {
    if (!id) return
    const res = await WepinBackendAPI.getInstance().accountToken.updateToken(
      { id },
      body,
    )
    if (res) {
      for (const [accountId, accountTokenList] of Object.entries(
        accountTokenListByAccountId.value,
      )) {
        if (!accountTokenList) continue
        const finded = accountTokenList.findIndex((accountToken) => {
          return accountToken.id === id
        })

        if (finded > -1) {
          accountTokenListByAccountId.value[accountId]?.splice(finded, 1)
          accountTokenListByAccountId.value[accountId]?.splice(
            finded,
            0,
            res.accountToken,
          )
        }
      }
    }
    return res
  }

  const removeAccountTokenWithAccountId = (accountId: IAccount['id']) => {
    delete accountTokenListByAccountId.value[accountId]
  }

  const fetchTokenList = getExecuteOnce(
    async (
      params?: IGetTokenListRequest,
      force: boolean | undefined = false,
    ) => {
      const cached = accountTokenListByAccountId.value[params?.accountIds!]
      if (!force && cached && cached.length >= 0) {
        return cached
      }

      if (force || !accountTokenListByAccountId.value[params?.accountIds!]) {
        const res =
          await WepinBackendAPI.getInstance().accountToken.getTokenList(params!)
        if (!res) return
        if (!accountTokenListByAccountId.value[params?.accountIds!]) {
          accountTokenListByAccountId.value[params?.accountIds!] = []
        }
        removeAccountTokenWithAccountId(params?.accountIds!)
        accountTokenListByAccountId.value[params?.accountIds!] =
          res.accountTokens
      }
      return accountTokenListByAccountId.value[params?.accountIds!]
    },
  )

  const getToken = getExecuteOnce(
    async (id: IAccountToken['id'], force: boolean | undefined = false) => {
      if (!id) return
      if (force) {
        const res = await WepinBackendAPI.getInstance().accountToken.getToken({
          id,
        })
        return res
      }
      return accountTokenList?.value!.find(
        (accountToken) => accountToken.accountId === id,
      )
    },
  )

  const reset = () => {
    accountTokenListByAccountId.value = {}
  }
  return {
    accountTokenList,
    accountTokenListByAccountId,
    addToken,
    deleteToken,
    updateToken,
    fetchTokenList,
    getToken,
    reset,
  }
})

export { useAccountTokenAPIStore }
